/**
 * AI Editor API Client
 *
 * API client for the AI structural editor.
 */

import { apiPost, apiGet } from './client';

// ============================================================================
// Types
// ============================================================================

export type StructuralAction =
  | 'add_section'
  | 'remove_section'
  | 'reorder_sections'
  | 'add_page'
  | 'remove_page'
  | 'update_navigation'
  | 'change_section_variant'
  | 'change_section_background'
  | 'add_service_item'
  | 'remove_service_item'
  | 'add_faq_item'
  | 'remove_faq_item'
  | 'add_testimonial'
  | 'remove_testimonial'
  | 'add_gallery_image'
  | 'remove_gallery_image'
  | 'swap_gallery_images'
  | 'move_gallery_image';

export interface StructuralTarget {
  page?: string;
  section?: string;
  elementPath?: string;
  index?: number;
}

export interface StructuralChange {
  id: string;
  action: StructuralAction;
  target: StructuralTarget;
  data: Record<string, unknown>;
  position?: number | 'start' | 'end' | 'after';
  afterSection?: string;
}

export interface AICommandIntent {
  action: StructuralAction;
  target: StructuralTarget;
  details: Record<string, unknown>;
  confidence: number;
}

export interface ChangePreview {
  description: string;
  changes: StructuralChange[];
}

export interface AICommandResponse {
  success: boolean;
  intent?: AICommandIntent;
  preview?: ChangePreview;
  clarification_needed: boolean;
  clarification_prompt?: string;
  error?: string;
}

export interface StructuralChangeResult {
  success: boolean;
  content?: unknown; // EditableSiteContent
  applied_changes?: StructuralChange[];
  error?: string;
}

export interface SectionSuggestionsResponse {
  suggestions: string[];
  existingSections: string[];
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Send a natural language command to the AI editor
 */
export async function sendAICommand(
  websiteId: string,
  command: string,
  context: {
    currentPage: string;
    selectedSection?: string;
    userIntent?: string;
  }
): Promise<AICommandResponse> {
  return apiPost<AICommandResponse>(`/api/staging/${websiteId}/ai-command`, {
    command,
    context,
  });
}

/**
 * Apply structural changes to the website
 */
export async function applyStructuralChanges(
  websiteId: string,
  changes: StructuralChange[]
): Promise<StructuralChangeResult> {
  return apiPost<StructuralChangeResult>(`/api/staging/${websiteId}/structural-change`, {
    changes,
  });
}

/**
 * Get section suggestions for the current page
 */
export async function getSectionSuggestions(
  websiteId: string,
  currentPage: string
): Promise<SectionSuggestionsResponse> {
  return apiGet<SectionSuggestionsResponse>(
    `/api/staging/${websiteId}/section-suggestions?page=${encodeURIComponent(currentPage)}`
  );
}
